import Box from 'assets/dynamic-bundle/box.svg';
import IconAcute from 'assets/icons/acute.svg';
import IconCheck from 'assets/icons/check-2.svg';
import IconClose from 'assets/icons/close-new.svg';
import cn from 'classnames';
import Button from 'components/button-new';
import { useGetBundleName } from 'components/dynamic-bundle/hooks/use-get-bundle-name';
import LazyMotionWrapper from 'components/lazy-motion-wrapper';
import PawLoader from 'components/paw-loader';
import { PriceComposition } from 'components/price-composition';
import { useConfig } from 'config/use-config';
import { getIntervalTranslationKeyShort, SubscriptionInterval } from 'constants/order';
import { useCart } from 'contexts/cart';
import { useDom } from 'contexts/dom';
import useContainsLowRateSubscription from 'hooks/cart/use-contains-low-rate-subscription';
import useBPPhysicalProductRedemption from 'hooks/common/use-bp-physical-product-redemption';
import useMobile from 'hooks/common/use-mobile';
import { useSavingsPlan } from 'hooks/savings-plan/use-savings-plan';
import { isGiftProductItem, isSubscriptionItem } from 'interfaces/line-item';
import {
  getBundleTotals,
  groupBundleItems,
} from 'modules/cart/components/cart-items/cart-bundle/utils';
import TinyCartItem from 'modules/cart/components/tiny-cart/item';
import { useCartState } from 'modules/cart/state';
import WrapperDesktop from 'modules/product-details/components/subscription-confirmation-information/wrapper-desktop';
import WrapperMobile from 'modules/product-details/components/subscription-confirmation-information/wrapper-mobile';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useClickAway, useMeasure } from 'react-use';
import { useSavingPlanStore } from 'stores/saving-plan';
import { isExpressCheckoutAvailable } from 'utils/cart';
import { generalImageLoader } from 'utils/image-loader';
import { calculateStrikePrice } from 'utils/product/calculate-strike-price';
import ExpressCheckoutButtons from '../summary/components/express-checkout-buttons';
import { PriceSummary } from './price-summary';
import { TrustedShop } from './trusted-shop';

interface SubscriptionConfirmationInformationProps {
  onCloseCallback?: () => void;
}

const TinyCartRenderer: FC = () => {
  const { effectiveCart, discountOrigins } = useCart();
  const { formatMessage } = useIntl();
  const groupedItems = useMemo(
    () => groupBundleItems(effectiveCart?.items || []),
    [effectiveCart?.items]
  );
  const getBundleName = useGetBundleName();

  if (!effectiveCart) return null;

  return groupedItems.map((item, index) => {
    const footerMessage = isSubscriptionItem(item)
      ? formatMessage({
          id: getIntervalTranslationKeyShort(item.interval as SubscriptionInterval) as string,
        })
      : formatMessage({ id: 'cart:interval-selection:one-off' });

    if ('bundleItems' in item) {
      const { totalWeight, totalDefaultPrice, totalPrice } = getBundleTotals({
        items: item.bundleItems,
        discountOrigins,
      });
      const { bundleName } = getBundleName(item.bundleId);

      return (
        <TinyCartItem
          data-test={`tiny-cart-item-bundle-${item.bundleId}`}
          key={`${item.bundleId}-${index}`}
          title={bundleName}
          truncateTitle
          price={
            <PriceComposition
              className="flex flex-col gap-0"
              currentPrice={totalPrice}
              priceBefore={totalDefaultPrice}
              color="primary"
              size="md"
              cents
            />
          }
          description={[
            {
              content: `${formatMessage({ id: 'common:product:variant' })}: ${totalWeight}g`,
              id: 'tiny-cart-item-variant',
            },
            {
              content: `${formatMessage({ id: 'common:product:quantity' })}: ${item.quantity}`,
              id: 'tiny-cart-item-quantity',
            },
          ]}
          footerMessage={footerMessage}
          image={
            <Box
              className="size-12"
              role="img"
              aria-label={formatMessage({ id: 'dynamic-bundle:illustration' })}
            />
          }
        />
      );
    }

    return (
      <TinyCartItem
        truncateTitle
        data-test={`tiny-cart-item-${isSubscriptionItem(item) ? 'subscription' : 'one-off'}`}
        key={`${item.productId}-${isSubscriptionItem(item)}-${isGiftProductItem(item)}`}
        title={item.productTitle}
        price={
          <PriceComposition
            className="flex flex-col gap-0"
            currentPrice={item.price}
            priceBefore={calculateStrikePrice(item, 1)}
            color="primary"
            size="md"
            cents
          />
        }
        description={[
          {
            content: `${formatMessage({ id: 'common:product:variant' })}: ${item.variantTitle}`,
            id: 'tiny-cart-item-variant',
          },
          {
            content: `${formatMessage({ id: 'common:product:quantity' })}: ${item.quantity}`,
            id: 'tiny-cart-item-quantity',
          },
        ]}
        footerMessage={footerMessage}
        image={
          <Image
            src={item.imageSrc || ''}
            alt={item.imageSrc || ''}
            quality={20}
            width={48}
            height={48}
            loader={generalImageLoader}
          />
        }
      />
    );
  });
};

/**
 * Tiny cart component used to display the cart in a tiny format shown at the header of the page
 */
const TinyCart: React.FC<SubscriptionConfirmationInformationProps> = ({ onCloseCallback }) => {
  const { featureFlags } = useConfig();
  const { effectiveCart } = useCart();
  const {
    isCartEmpty,
    isCheckoutBeingCreated,
    itemsCount,
    shouldShowMinimumBasketNotice,
    goToCheckout,
    isCartUpdating,
    oosItems,
  } = useCartState();
  const activePlan = useSavingPlanStore((state) => state.activePlan);
  const { isPlanInCart } = useSavingsPlan();
  const [isOpen, setIsOpen] = useState(true);
  const mobile = useMobile();
  const { push } = useRouter();
  const { hasEnoughPoint, shouldShowGiftMovMessage } = useBPPhysicalProductRedemption({
    effectiveCart,
  });
  const handleClose = useCallback(() => {
    setIsOpen(false);
    // wait until animation is done
    const timeoutId = setTimeout(() => {
      onCloseCallback && onCloseCallback();
    }, 1500);

    return () => clearTimeout(timeoutId);
  }, [onCloseCallback]);

  const Wrapper = mobile ? WrapperMobile : WrapperDesktop;

  const expressCheckoutAvailable = isExpressCheckoutAvailable(
    featureFlags.expressCheckout,
    effectiveCart,
    isPlanInCart || !!activePlan
  );

  const disableExpressCheckoutButtons =
    isCartEmpty || shouldShowMinimumBasketNotice || shouldShowGiftMovMessage || !hasEnoughPoint;

  const { containsLowRateSubscription } = useContainsLowRateSubscription({
    effectiveCart,
  });

  const isCheckoutDisabled =
    isCartEmpty ||
    shouldShowMinimumBasketNotice ||
    isCheckoutBeingCreated ||
    shouldShowGiftMovMessage ||
    !hasEnoughPoint ||
    containsLowRateSubscription ||
    oosItems.length > 0;

  const { headerCartIconRef } = useDom();

  const [minutes, setMinutes] = useState(60);

  useEffect(() => {
    const timer = setInterval(() => {
      setMinutes((prev) => (prev > 0 ? prev - 1 : 0));
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const [bottomRef, { height: bottomHeight }] = useMeasure<HTMLDivElement>();
  const [containerRef, { height: containerHeight }] = useMeasure<HTMLDivElement>();
  const [headerRef, { height: headerHeight }] = useMeasure<HTMLDivElement>();

  const ref = useRef(null);
  useClickAway(ref, handleClose);

  /**
   * Disable scroll hide when cart is open
   */
  const { setDisableScrollHide } = useDom();
  useEffect(() => {
    if (isOpen) {
      setDisableScrollHide(true);
    }
    return () => {
      setDisableScrollHide(false);
    };
  }, [isOpen, setDisableScrollHide]);

  if (!effectiveCart) return null;

  return (
    <LazyMotionWrapper>
      <Wrapper isOpen={isOpen} onClose={handleClose} iconRef={headerCartIconRef}>
        <div ref={ref} data-test="tiny-cart-container" className="bg-other-white">
          <div
            className={cn(
              'relative max-h-[calc(100vh-var(--header-height))] px-3',
              mobile && `max-h-[90vh]!`
            )}
            ref={containerRef}
            data-test="tiny-cart-content"
          >
            <div
              ref={headerRef}
              className={cn(mobile && 'sticky top-0 z-10')}
              data-test="tiny-cart-header"
            >
              <div
                className="mb-2 flex items-center justify-between"
                data-test="tiny-cart-header-row"
              >
                <div className="flex items-center gap-1" data-test="tiny-cart-title-container">
                  <span className="text-text-light-bg-primary font-black" data-test="cart-title">
                    <FormattedMessage id="cart:title" />
                  </span>

                  <span className="text-text-light-bg-secondary text-sm" data-test="cart-sub-title">
                    <FormattedMessage
                      id="cart:sub-title"
                      values={{
                        value: itemsCount,
                      }}
                    />
                  </span>
                </div>
                <button
                  className="ml-auto hidden items-center lg:block"
                  onClick={handleClose}
                  data-test="tiny-cart-close-button"
                >
                  <IconClose className="text-text-secondary h-6 w-6" />
                </button>
              </div>
              <div
                className="text-success-dark bg-success-background mb-4 flex items-center rounded-md px-1 py-2 text-sm font-medium"
                data-test="tiny-cart-success-message"
              >
                <IconCheck className="mr-1 inline-block h-4 w-4" />
                <FormattedMessage id="pdp:subscription-confirmation-information:success" />
              </div>
            </div>
            <div
              className="bg-grey-50 -mx-3 flex flex-col gap-2 overflow-y-auto p-3"
              style={{
                maxHeight: containerHeight - headerHeight - bottomHeight,
              }}
              data-test="cart-items-container"
            >
              {isCartUpdating ? (
                <div
                  className="flex items-center justify-center"
                  data-test="loader"
                  style={{
                    minHeight: 100,
                  }}
                >
                  <PawLoader />
                </div>
              ) : (
                <>
                  <TinyCartRenderer />
                </>
              )}
            </div>
            <div
              className="bg-other-white sticky top-0 right-0 bottom-0 left-0 -mx-3 px-3"
              ref={bottomRef}
              data-test="tiny-cart-bottom-section"
              style={{
                boxShadow: '0px -2px 4px rgba(0, 0, 0, 0.05)',
              }}
            >
              <PriceSummary />

              <div
                className="border-other-divider mb-4 -ml-3 w-[calc(100%+1.5rem)] border-t border-dashed"
                data-test="tiny-cart-divider"
              />

              <div
                className="bg-info-light text-info-dark flex items-center gap-2 rounded-md p-2 text-xs"
                data-test="urgency-message"
              >
                <IconAcute />
                <span>
                  <FormattedMessage id="cart:tiny-cart:urgency-note" values={{ minutes }} />
                </span>
              </div>

              <div className="flex gap-2" data-test="tiny-cart-buttons-container">
                <Button
                  size="base"
                  layout={!isCheckoutDisabled ? 'outline' : 'contained'}
                  className={cn('mt-4', !isCheckoutDisabled && 'w-36!')}
                  onClick={() => {
                    push('/cart');
                  }}
                  data-test="view-cart-button"
                >
                  <FormattedMessage id="cart:title" />
                </Button>
                {!isCheckoutDisabled && (
                  <Button
                    size="base"
                    layout={'contained'}
                    className="mt-4"
                    disabled={isCheckoutDisabled}
                    styleDisabled={isCheckoutDisabled}
                    onClick={goToCheckout}
                    data-test="checkout-button"
                  >
                    <FormattedMessage id="cart:go-to-checkout" />
                  </Button>
                )}
              </div>
              <div data-test="tiny-cart-express-checkout-section">
                {!isCheckoutDisabled && expressCheckoutAvailable && (
                  <>
                    <div
                      className="text-text-light-bg-secondary my-2 text-center text-sm font-medium"
                      data-test="express-checkout-text"
                    >
                      <FormattedMessage id="cart:summary:pay-also-with-test" />
                    </div>

                    <ExpressCheckoutButtons
                      disabled={disableExpressCheckoutButtons}
                      data-test="express-checkout-buttons"
                    />
                  </>
                )}
              </div>
              <div
                className="bg-background-default mt-4 -ml-3 w-[calc(100%+1.5rem)] py-2"
                data-test="tiny-cart-trusted-shop-container"
              >
                <TrustedShop className="px-2" />
              </div>
            </div>
          </div>
        </div>
      </Wrapper>
    </LazyMotionWrapper>
  );
};

export default TinyCart;
